import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'antd';
import { CheckOutlined, LikeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { FeedReaction } from '../../lib/feedInteractions';
import { trackConversion } from '../../lib/conversionEvents';
import { feedSubjectIds, playerName } from '../../shared/utils/league-feed';
import {
  CONGRATS_SEEN_EVENT,
  congratsSince,
  returnCongratsRows,
  saveCongratsSeen,
} from '../../shared/utils/feed-congrats-inbox';

const REACTABLE: LeagueFeed.Type[] = ['katildi', 'mac_sonucu', 'kilometre_tasi', 'gonderi'];

type Props = {
  viewerId: string;
  // Akışta görünen (gizlenmemiş) olaylar.
  events: LeagueFeed.Event[];
  reactions: Record<string, FeedReaction>;
  // Tepki durumu bu akış için yüklendi mi (eski akışın durumu kullanılmaz).
  ready: boolean;
  // T7: akış üstünde tek görev modülü; seçilmediyse yalnız "N yeni tebrik" bilgisi kalır.
  showRows: boolean;
  // eslint-disable-next-line no-unused-vars -- callback type signature
  onCongrats: (event: LeagueFeed.Event) => Promise<boolean>;
  // eslint-disable-next-line no-unused-vars -- callback type signature
  onOpen: (event: LeagueFeed.Event) => void;
};

/**
 * Ç7 + Ç1 (akış araştırması, 3 Kasım 2026): "Son ziyaretinden beri N yeni tebrik aldın" ve
 * seni tebrik edenlerin tebrik etmediğin son kartı ("Sen de onu tebrik et"). Karşılık alan
 * oyuncu karşılık verir (Eckles 2016; canlı veride 5×). Son görülen sayılar yalnız bu tarayıcıda;
 * kayıt yoksa ilk ziyarette çip çıkmaz. Başarı sunucu onayından sonra gösterilir.
 */
export default function FeedCongratsInbox({
  viewerId,
  events,
  reactions,
  ready,
  showRows,
  onCongrats,
  onOpen,
}: Props) {
  const { t } = useTranslation('leagues');
  const [inbox, setInbox] = useState<{ count: number; first: LeagueFeed.Event | null } | null>(
    null,
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);
  const own = useMemo(
    () =>
      events.filter(
        event => REACTABLE.includes(event.tur) && feedSubjectIds(event).includes(viewerId),
      ),
    [events, viewerId],
  );

  // Çip ziyaret başına bir kez hesaplanır; sonra güncel sayılar saklanır.
  useEffect(() => {
    if (inbox || !own.length) return;
    const result = congratsSince(viewerId, own);
    setInbox({
      count: result.count,
      first: own.find(event => event.id === result.firstId) || null,
    });
    saveCongratsSeen(viewerId, own);
    // Ç7: üst bar rozeti sıfırlanır (akış görüldü).
    window.dispatchEvent(new Event(CONGRATS_SEEN_EVENT));
  }, [own, viewerId, inbox]);

  const rows = useMemo(
    () =>
      ready && showRows
        ? returnCongratsRows(
            events,
            viewerId,
            event => reactions[event.id]?.selected,
            Date.now(),
            done,
          )
        : [],
    [events, reactions, ready, showRows, viewerId, done],
  );

  if (!(inbox?.count ?? 0) && !rows.length) return null;

  const congrats = async (event: LeagueFeed.Event) => {
    setBusy(event.id);
    setFailed(false);
    const ok = await onCongrats(event);
    setBusy(null);
    if (ok) {
      setDone(current => [...current, event.id]);
      trackConversion('feed_return_congrats');
    } else setFailed(true);
  };

  return (
    <section className="feed-catchup feed-inbox" aria-labelledby="feed-inbox-title">
      <h3 id="feed-inbox-title">
        {inbox?.count
          ? t('detail.feed.inboxTitle', { count: inbox.count })
          : t('detail.feed.returnTitle')}
        {inbox?.count && inbox.first ? (
          <Button type="link" className="feed-inbox__open" onClick={() => onOpen(inbox.first!)}>
            {t('detail.feed.inboxOpen')}
          </Button>
        ) : null}
      </h3>
      {rows.length > 0 && (
        <ul>
          {rows.map(({ reactor, event }) => {
            const name = playerName({ ...reactor, null_seviye: null, verified_account: false });
            return (
              <li key={reactor.id}>
                <button type="button" className="feed-catchup__match" onClick={() => onOpen(event)}>
                  {t('detail.feed.returnRow', { name })}
                </button>
                {done.includes(event.id) ? (
                  <span className="feed-catchup__done" role="status">
                    <CheckOutlined aria-hidden="true" /> {t('detail.feed.congratsDone')}
                  </span>
                ) : (
                  <Button
                    className="feed-catchup__clap"
                    loading={busy === event.id}
                    disabled={!!busy && busy !== event.id}
                    onClick={() => congrats(event)}
                    aria-label={t('detail.feed.returnCongrats', { name })}
                  >
                    <LikeOutlined aria-hidden="true" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {failed && (
        <p className="feed-catchup__error" role="alert">
          {t('detail.feed.congratsError')}
        </p>
      )}
    </section>
  );
}
